import { create } from 'zustand'
import type { Dataset } from '@/types/dataset'
import { datasetsApi } from '@/services/api/datasets'

interface DatasetStore {
  datasets: Dataset[]
  currentDataset: Dataset | null
  isLoading: boolean
  error: string | null
  loadDatasets: () => Promise<void>
  loadDataset: (id: string) => Promise<void>
  uploadDataset: (file: File, name: string, metadata?: Record<string, unknown>) => Promise<Dataset>
  deleteDataset: (id: string) => Promise<void>
  setCurrentDataset: (dataset: Dataset | null) => void
}

export const useDatasetStore = create<DatasetStore>((set) => ({
  datasets: [],
  currentDataset: null,
  isLoading: false,
  error: null,

  loadDatasets: async () => {
    set({ isLoading: true, error: null })
    try {
      const result = await datasetsApi.list()
      // Extract datasets array from paginated response
      const datasetsArray = result.datasets || []
      set({ datasets: datasetsArray, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load datasets',
        isLoading: false,
        datasets: [],
      })
    }
  },

  loadDataset: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      const dataset = await datasetsApi.get(id)
      set({ currentDataset: dataset, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load dataset',
        isLoading: false,
      })
    }
  },

  uploadDataset: async (file: File, name: string, metadata?: Record<string, unknown>) => {
    set({ isLoading: true, error: null })
    try {
      const dataset = await datasetsApi.upload({ file, name, metadata })
      set((state) => ({
        datasets: [...state.datasets, dataset],
        isLoading: false,
      }))
      return dataset
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to upload dataset',
        isLoading: false,
      })
      throw error
    }
  },

  deleteDataset: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      await datasetsApi.delete(id)
      set((state) => ({
        datasets: state.datasets.filter((d) => d.id !== id),
        currentDataset: state.currentDataset?.id === id ? null : state.currentDataset,
        isLoading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete dataset',
        isLoading: false,
      })
      throw error
    }
  },

  setCurrentDataset: (dataset: Dataset | null) => {
    set({ currentDataset: dataset })
  },
}))
